import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import {
    Bot,
    Clock3,
    Loader2,
    MessageSquarePlus,
    Plus,
    Search,
    Send,
    Sparkles,
    Trash2,
    X,
} from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    chatStore,
    ChatConversationSummary,
    ChatMessage,
    ChatToolCall,
    initialChatMessages,
} from '../stores/chat_store'
import { Toasts } from '../components/toaster/Toast'
import {
    tsBulkActionsDelete,
    tsBulkActionsGet,
    tsBulkActionsPatch,
    tsBulkActionsPost,
} from '../util/api'
import { useUser } from '../hooks/useUser'

type SendMessageOptions = {
    appendUserMessage?: boolean
    content?: string
}

type MarkdownBlock =
    | { type: 'code'; language: string | null; content: string }
    | { type: 'heading'; level: 1 | 2 | 3 | 4; content: string }
    | { type: 'list'; items: string[] }
    | { type: 'paragraph'; content: string }
    | { type: 'rule' }
    | { type: 'table'; headers: string[]; rows: string[][] }

type ChatLimits = {
    maxMessages: number
    maxMessageChars: number
    maxTotalChars: number
}

const fallbackChatLimits: ChatLimits = {
    maxMessages: 40,
    maxMessageChars: 16000,
    maxTotalChars: 64000,
}

const unwrapResponse = <T,>(response: any): T => (response?.data ?? response) as T

const asStringId = (value: unknown): string => String(value ?? '')

const getRequestErrorMessage = (error: unknown, fallback: string): string => {
    const data = (error as any)?.response?.data
    const message = data?.message ?? data?.error?.message

    if (typeof message === 'string' && message.trim()) {
        return message
    }

    return error instanceof Error ? error.message : fallback
}

const normalizeConversation = (conversation: any): ChatConversationSummary => ({
    id: asStringId(conversation.id ?? conversation.runId ?? conversation.run_id),
    title: conversation.title ?? 'Untitled conversation',
    model: conversation.model,
    status: conversation.status,
    createdAt: conversation.createdAt ?? conversation.created_at,
    updatedAt: conversation.updatedAt ?? conversation.updated_at,
    toolCallCount: conversation.toolCallCount ?? conversation.tool_call_count,
})

const normalizeMessage = (message: any): ChatMessage | null => {
    if (!message || (message.role !== 'user' && message.role !== 'assistant')) {
        return null
    }

    return {
        role: message.role,
        content: String(message.content ?? ''),
        toolCalls: message.toolCalls ?? message.tool_calls,
    }
}

export const ChatPage = observer(() => {
    const navigate = useNavigate()
    const { conversationId } = useParams()
    const { activeProfile } = useUser()
    const routeConversationId = conversationId ?? null
    const activeProfileId = activeProfile?.profile_id ?? null
    const chat = chatStore
    const messages = chat.messages
    const conversations = chat.conversations
    const currentRunId = chat.currentRunId
    const isSending = chat.isSending
    const queuedMessages = chat.queuedMessages
    const error = chat.error
    const conversationSearch = chat.conversationSearch
    const [renamingConversationId, setRenamingConversationId] = useState<string | null>(null)
    const [renameDraft, setRenameDraft] = useState('')
    const [chatLimits, setChatLimits] = useState<ChatLimits>(fallbackChatLimits)
    const messagesEndRef = useRef<HTMLDivElement | null>(null)
    const messagesRef = useRef(messages)
    const currentRunIdRef = useRef(currentRunId)
    const isSendingRef = useRef(isSending)
    const conversationVersionRef = useRef(0)
    const shouldSnapToMessageEndRef = useRef(false)
    const chatRequestOptions = useMemo(
        () =>
            activeProfileId
                ? {
                      ignore: true,
                      headers: { ProfileId: String(activeProfileId) },
                  }
                : null,
        [activeProfileId],
    )

    const scrollToMessageEnd = (behavior: ScrollBehavior) => {
        const scroll = () => {
            messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' })
        }

        scroll()
        window.requestAnimationFrame(() => {
            scroll()
            window.requestAnimationFrame(scroll)
        })
    }

    const loadConversations = async () => {
        if (!chatRequestOptions) return

        chat.setIsLoadingConversations(true)

        try {
            const response = await tsBulkActionsGet('chat/runs', null, chatRequestOptions)
            const data = unwrapResponse<any>(response)
            const rawConversations = data.conversations ?? data.runs ?? data.data ?? data ?? []
            chat.setConversations(
                Array.isArray(rawConversations) ? rawConversations.map(normalizeConversation) : [],
            )
        } catch {
            chat.setConversations([])
        } finally {
            chat.setIsLoadingConversations(false)
        }
    }

    const loadChatConfig = async () => {
        if (!chatRequestOptions) return

        try {
            const response = await tsBulkActionsGet('chat/config', null, chatRequestOptions)
            const data = unwrapResponse<any>(response)

            if (data.limits && typeof data.limits === 'object') {
                setChatLimits({
                    maxMessages:
                        typeof data.limits.maxMessages === 'number'
                            ? data.limits.maxMessages
                            : fallbackChatLimits.maxMessages,
                    maxMessageChars:
                        typeof data.limits.maxMessageChars === 'number'
                            ? data.limits.maxMessageChars
                            : fallbackChatLimits.maxMessageChars,
                    maxTotalChars:
                        typeof data.limits.maxTotalChars === 'number'
                            ? data.limits.maxTotalChars
                            : fallbackChatLimits.maxTotalChars,
                })
            }
        } catch {
            setChatLimits(fallbackChatLimits)
        }
    }

    const loadConversation = async (id: string) => {
        if (!chatRequestOptions) return

        chat.hydratePersistedInput(id)

        try {
            const response = await tsBulkActionsGet(`chat/runs/${id}`, null, chatRequestOptions)
            const data = unwrapResponse<any>(response)
            const conversation = data.conversation ?? data.run ?? data.data ?? data
            const normalizedMessages = (conversation.messages ?? [])
                .map(normalizeMessage)
                .filter(Boolean) as ChatMessage[]
            const loadedMessages = [...initialChatMessages, ...normalizedMessages]

            shouldSnapToMessageEndRef.current = true
            messagesRef.current = loadedMessages
            chat.setMessages(loadedMessages)
            conversationVersionRef.current += 1
            currentRunIdRef.current = asStringId(conversation.id ?? id)
            chat.setCurrentRunId(asStringId(conversation.id ?? id))
            chat.setCurrentTitle(conversation.title ?? 'Untitled conversation')
        } catch (requestError) {
            Toasts.error(getRequestErrorMessage(requestError, 'Conversation load failed'))
            if (routeConversationId === id) {
                navigate('/chat', { replace: true })
            }
        }
    }

    useEffect(() => {
        if (!activeProfileId) return

        loadChatConfig()
        loadConversations()
    }, [activeProfileId])

    useEffect(() => {
        if (!activeProfileId) return

        if (routeConversationId) {
            chat.setRouteContext(routeConversationId)
            loadConversation(routeConversationId)
            return
        }

        if (chat.hasActiveConversation) {
            chat.setRouteContext(chat.currentRunId)

            if (chat.currentRunId) {
                navigate(`/chat/${chat.currentRunId}`, { replace: true })
            }

            return
        }

        chat.setRouteContext(null)
    }, [activeProfileId, routeConversationId])

    useEffect(() => {
        messagesRef.current = messages
    }, [messages])

    useEffect(() => {
        currentRunIdRef.current = currentRunId
    }, [currentRunId])

    useEffect(() => {
        isSendingRef.current = isSending
    }, [isSending])

    useEffect(() => {
        const behavior = shouldSnapToMessageEndRef.current ? 'auto' : 'smooth'
        shouldSnapToMessageEndRef.current = false
        scrollToMessageEnd(behavior)
    }, [isSending, messages])

    const startNewConversation = (options: { updateUrl?: boolean } = {}) => {
        conversationVersionRef.current += 1
        messagesRef.current = [...initialChatMessages]
        chat.resetActiveConversation()
        currentRunIdRef.current = null

        if (options.updateUrl ?? true) {
            navigate('/chat')
        }
    }

    const deleteConversation = async (conversation: ChatConversationSummary) => {
        if (!chatRequestOptions) return

        if (!window.confirm(`Delete "${conversation.title}"? This cannot be undone.`)) {
            return
        }

        try {
            await tsBulkActionsDelete(`chat/runs/${conversation.id}`, null, chatRequestOptions)
            chat.removeConversation(conversation.id)

            if (currentRunId === conversation.id) {
                startNewConversation()
            }
        } catch (requestError) {
            Toasts.error(getRequestErrorMessage(requestError, 'Conversation delete failed'))
        }
    }

    const saveConversationTitle = async (conversation: ChatConversationSummary) => {
        if (!chatRequestOptions) return

        const title = renameDraft.trim()

        if (!title) {
            setRenamingConversationId(null)
            setRenameDraft('')
            return
        }

        try {
            const response = await tsBulkActionsPatch(
                `chat/runs/${conversation.id}`,
                { title },
                chatRequestOptions,
            )
            const data = unwrapResponse<any>(response)
            const updated = normalizeConversation(
                data.conversation ?? data.run ?? data.data ?? { ...conversation, title },
            )
            chat.updateConversation(updated)

            if (currentRunId === conversation.id) {
                chat.setCurrentTitle(updated.title)
            }
        } catch (requestError) {
            Toasts.error(getRequestErrorMessage(requestError, 'Conversation rename failed'))
        } finally {
            setRenamingConversationId(null)
            setRenameDraft('')
        }
    }

    const sendMessage = async (options: SendMessageOptions = {}) => {
        if (!chatRequestOptions) {
            Toasts.error('Profile is still loading. Try again in a moment.')
            return
        }

        const content = (options.content ?? chat.draft).trim()
        if (!content) return
        if (content.length > chatLimits.maxMessageChars) {
            Toasts.error(
                `Messages must be ${chatLimits.maxMessageChars.toLocaleString()} characters or fewer.`,
            )
            return
        }

        const shouldAppendUserMessage = options.appendUserMessage ?? true
        if (isSendingRef.current) {
            if (shouldAppendUserMessage) chat.addQueuedMessage(content)
            return
        }

        const nextMessages: ChatMessage[] = shouldAppendUserMessage
            ? [...messagesRef.current, { role: 'user', content }]
            : [...messagesRef.current]
        const apiMessages = nextMessages.filter(
            (message, index) =>
                index > 0 && (message.role === 'user' || message.role === 'assistant'),
        )
        const totalChars = apiMessages.reduce((sum, message) => sum + message.content.length, 0)

        if (apiMessages.length > chatLimits.maxMessages) {
            Toasts.error(
                `Conversations can include up to ${chatLimits.maxMessages.toLocaleString()} messages.`,
            )
            return
        }

        if (totalChars > chatLimits.maxTotalChars) {
            Toasts.error(
                `Conversation history must be ${chatLimits.maxTotalChars.toLocaleString()} characters or fewer.`,
            )
            return
        }

        messagesRef.current = nextMessages
        chat.setMessages(nextMessages)
        if (shouldAppendUserMessage) chat.clearDraft()

        isSendingRef.current = true
        chat.setIsSending(true)
        chat.setError(null)
        const requestConversationVersion = conversationVersionRef.current

        try {
            const response = await tsBulkActionsPost(
                'chat/messages',
                {
                    ...(currentRunIdRef.current ? { runId: currentRunIdRef.current } : {}),
                    messages: apiMessages.map((message) => ({
                        role: message.role,
                        content: message.content,
                    })),
                },
                chatRequestOptions,
            )
            const data = unwrapResponse<any>(response)

            if (requestConversationVersion !== conversationVersionRef.current) {
                loadConversations()
                return
            }

            const runId = data.runId ?? data.run_id ?? data.conversationId ?? data.conversation_id
            if (runId) {
                const isNewRun = !currentRunIdRef.current
                currentRunIdRef.current = asStringId(runId)
                chat.setCurrentRunId(asStringId(runId), { transferPersistedInput: isNewRun })
                if (
                    routeConversationId !== asStringId(runId) &&
                    window.location.pathname.startsWith('/chat')
                ) {
                    navigate(`/chat/${asStringId(runId)}`, { replace: isNewRun })
                }
            }

            if (data.conversationTitle ?? data.title) {
                chat.setCurrentTitle(data.conversationTitle ?? data.title)
            }

            const responseToolCalls =
                data.message?.toolCalls ??
                data.message?.tool_calls ??
                data.toolCalls ??
                data.tool_calls
            const assistantMessage = normalizeMessage({
                ...(data.message ?? {}),
                toolCalls: responseToolCalls,
            }) ?? {
                role: 'assistant',
                content: String(data.content ?? data.response ?? ''),
                toolCalls: responseToolCalls,
            }
            const completedMessages = [...nextMessages, assistantMessage]
            messagesRef.current = completedMessages
            chat.setMessages(completedMessages)
            loadConversations()
        } catch (requestError) {
            if (requestConversationVersion === conversationVersionRef.current) {
                const message = getRequestErrorMessage(requestError, 'Chat request failed')
                chat.setError(message)
                Toasts.error(message)
            }
        } finally {
            isSendingRef.current = false
            chat.setIsSending(false)
        }
    }

    useEffect(() => {
        if (isSending || error || queuedMessages.length === 0) return

        const [nextQueuedMessage] = queuedMessages
        if (!nextQueuedMessage) return

        chat.shiftQueuedMessage(nextQueuedMessage.id)
        sendMessage({ content: nextQueuedMessage.content })
    }, [error, isSending, queuedMessages])

    const visibleMessages = messages.slice(1)
    const filteredConversations = useMemo(
        () =>
            conversations
                .filter((conversation) =>
                    conversation.title.toLowerCase().includes(conversationSearch.toLowerCase()),
                )
                .sort((a, b) => Date.parse(b.updatedAt ?? '') - Date.parse(a.updatedAt ?? '')),
        [conversations, conversationSearch],
    )

    return (
        <section className="flex h-screen min-h-0 bg-[#fbfcfd] text-[#202124]">
            <ConversationSidebar
                currentRunId={currentRunId}
                filteredConversations={filteredConversations}
                renamingConversationId={renamingConversationId}
                renameDraft={renameDraft}
                setRenamingConversationId={setRenamingConversationId}
                setRenameDraft={setRenameDraft}
                saveConversationTitle={saveConversationTitle}
                deleteConversation={deleteConversation}
                startNewConversation={startNewConversation}
            />

            <div className="relative flex min-w-0 flex-1 justify-center overflow-hidden px-5">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_16%_0%,rgb(45_114_210/0.045),transparent_34%),radial-gradient(circle_at_82%_0%,rgb(35_133_81/0.035),transparent_34%),linear-gradient(180deg,rgb(255_255_255/0.6),transparent)]" />
                <div className="relative flex min-h-0 w-full max-w-5xl flex-col">
                    <div className="flex-1 overflow-y-auto py-8">
                        {visibleMessages.length === 0 ? (
                            <EmptyChatState onPrompt={(prompt) => chat.setDraft(prompt)} />
                        ) : (
                            <div className="mx-auto max-w-3xl space-y-7 pb-6">
                                {visibleMessages.map((message, index) => (
                                    <ChatMessageBubble
                                        key={`${message.role}-${String(index)}`}
                                        message={message}
                                    />
                                ))}
                            </div>
                        )}

                        <div className="mx-auto max-w-3xl">
                            {isSending ? <ThinkingIndicator /> : null}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    <ChatComposer
                        chatLimits={chatLimits}
                        onSendMessage={(content) => sendMessage({ content })}
                    />
                </div>
            </div>
        </section>
    )
})

type SidebarProps = {
    currentRunId: string | null
    filteredConversations: ChatConversationSummary[]
    renamingConversationId: string | null
    renameDraft: string
    setRenamingConversationId: (id: string | null) => void
    setRenameDraft: (draft: string) => void
    saveConversationTitle: (conversation: ChatConversationSummary) => void
    deleteConversation: (conversation: ChatConversationSummary) => void
    startNewConversation: () => void
}

const ConversationSidebar = observer(
    ({
        currentRunId,
        filteredConversations,
        renamingConversationId,
        renameDraft,
        setRenamingConversationId,
        setRenameDraft,
        saveConversationTitle,
        deleteConversation,
        startNewConversation,
    }: SidebarProps) => {
        const navigate = useNavigate()
        const chat = chatStore

        return (
            <aside className="flex h-screen w-72 shrink-0 flex-col border-r border-black/10 bg-white/82 backdrop-blur-xl">
                <div className="border-b border-black/10 p-3">
                    <button
                        className="flex h-9 w-full items-center justify-between rounded-md border border-black/10 bg-white px-2.5 text-sm font-medium shadow-[0_1px_1px_rgb(0_0_0/0.04)] transition hover:bg-[#f8f9fb]"
                        onClick={startNewConversation}
                        type="button"
                    >
                        <span className="flex items-center gap-2">
                            <Plus className="size-4 text-[#2d72d2]" />
                            New conversation
                        </span>
                    </button>
                    <label className="mt-2 flex h-9 items-center gap-2 rounded-md border border-black/10 bg-white px-2 text-sm shadow-[0_1px_1px_rgb(0_0_0/0.04)]">
                        <Search className="size-3.5 text-[#8a8b91]" />
                        <input
                            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#8a8b91]"
                            onChange={(event) => chat.setConversationSearch(event.target.value)}
                            placeholder="Search conversations"
                            value={chat.conversationSearch}
                        />
                    </label>
                </div>

                <div className="flex-1 overflow-y-auto px-3 py-3">
                    <div className="mb-2 flex items-center justify-between px-1 text-xs font-medium text-[#8a8b91]">
                        <span>Recent</span>
                        {chat.isLoadingConversations ? <span>Loading...</span> : null}
                    </div>
                    <div className="space-y-1">
                        {filteredConversations.length === 0 && !chat.isLoadingConversations ? (
                            <div className="px-1 py-2 text-xs leading-5 text-[#8a8b91]">
                                {chat.conversationSearch
                                    ? 'No conversations match that search.'
                                    : 'No conversations yet'}
                            </div>
                        ) : null}
                        {filteredConversations.map((conversation) => (
                            <div
                                key={conversation.id}
                                className={classNames(
                                    'group flex h-8 w-full items-center rounded-md text-sm transition',
                                    currentRunId === conversation.id
                                        ? 'bg-[#eef2f7] text-[#151619] shadow-[inset_0_0_0_1px_rgb(0_0_0/0.03)]'
                                        : 'text-[#4b4c52] hover:bg-[#f5f6f8] hover:text-[#151619]',
                                )}
                            >
                                <div className="flex h-full min-w-0 flex-1 items-center px-2">
                                    {renamingConversationId === conversation.id ? (
                                        <input
                                            autoFocus
                                            className="h-6 min-w-0 flex-1 rounded border border-[#9dbdff] bg-white px-1.5 text-sm outline-none ring-2 ring-[#9dbdff]/20"
                                            onBlur={() => {
                                                setRenamingConversationId(null)
                                                setRenameDraft('')
                                            }}
                                            onChange={(event) => setRenameDraft(event.target.value)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Escape') {
                                                    event.preventDefault()
                                                    setRenamingConversationId(null)
                                                    setRenameDraft('')
                                                }

                                                if (event.key === 'Enter') {
                                                    event.preventDefault()
                                                    saveConversationTitle(conversation)
                                                }
                                            }}
                                            value={renameDraft}
                                        />
                                    ) : (
                                        <button
                                            className="flex h-full min-w-0 flex-1 items-center text-left"
                                            onClick={() => navigate(`/chat/${conversation.id}`)}
                                            onDoubleClick={(event) => {
                                                event.preventDefault()
                                                setRenamingConversationId(conversation.id)
                                                setRenameDraft(conversation.title)
                                            }}
                                            title={conversation.title}
                                            type="button"
                                        >
                                            <span className="block min-w-0 flex-1 truncate">
                                                {conversation.title}
                                            </span>
                                        </button>
                                    )}
                                </div>
                                <button
                                    aria-label={`Delete ${conversation.title}`}
                                    className="mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-[#8a8b91] opacity-0 transition hover:bg-white/80 hover:text-red-600 group-hover:opacity-100"
                                    onClick={() => deleteConversation(conversation)}
                                    type="button"
                                >
                                    <Trash2 className="size-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </aside>
        )
    },
)

const EmptyChatState = ({ onPrompt }: { onPrompt: (prompt: string) => void }) => {
    const prompts = [
        {
            label: 'Account performance',
            prompt: 'Summarize the biggest opportunities in this ad account over the last 30 days.',
        },
        {
            label: 'Recipe ideas',
            prompt: 'Suggest automation recipes for campaigns with high ACoS and enough spend.',
        },
        {
            label: 'Bulk changes',
            prompt: 'Help me identify safe bid changes for keywords with strong conversion rates.',
        },
    ]

    return (
        <div className="mx-auto flex min-h-[46vh] max-w-3xl flex-col items-center justify-center text-center">
            <div className="mb-5 flex size-11 items-center justify-center rounded-xl border border-black/10 bg-white/80 shadow-[0_6px_22px_rgb(15_23_42/0.06)]">
                <Bot className="size-5 text-[#2d72d2]" />
            </div>
            <h1 className="text-3xl font-semibold tracking-normal text-[#25262a] sm:text-4xl">
                Ask Merch Jar AI.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-[#77777d]">
                Explore account performance, build automation ideas, and reason through changes
                before applying them.
            </p>
            <div className="mt-8 grid w-full gap-2 sm:grid-cols-3">
                {prompts.map((item) => (
                    <button
                        key={item.label}
                        className="min-h-20 rounded-xl border border-black/10 bg-white/70 px-4 py-3 text-left text-sm text-[#5f6066] shadow-[0_4px_18px_rgb(15_23_42/0.035)] transition hover:border-[#9dbdff] hover:bg-white"
                        onClick={() => onPrompt(item.prompt)}
                        type="button"
                    >
                        <span className="font-medium text-[#2d2e32]">{item.label}</span>
                        <span className="mt-1 block text-xs leading-5 text-[#8f9096]">
                            {item.prompt}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    )
}

const ChatMessageBubble = ({ message }: { message: ChatMessage }) => {
    if (message.role === 'user') {
        return (
            <div className="ml-auto max-w-[82%] rounded-2xl bg-[#eef4ff] px-4 py-3 text-sm leading-6 text-[#254d8d] shadow-[0_1px_0_rgb(37_99_235/0.06)]">
                <p className="whitespace-pre-wrap">{message.content}</p>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-[2rem_1fr] gap-3">
            <span className="mt-1 flex size-7 items-center justify-center rounded-md bg-[#111] text-white">
                <Sparkles className="size-3.5" />
            </span>
            <div className="min-w-0">
                <p className="mb-1 text-xs font-medium uppercase tracking-[0.08em] text-[#98999f]">
                    Merch Jar AI
                </p>
                {message.toolCalls && message.toolCalls.length > 0 ? (
                    <ToolCallList toolCalls={message.toolCalls} />
                ) : null}
                <MarkdownMessage content={message.content} />
            </div>
        </div>
    )
}

const ThinkingIndicator = () => (
    <div className="grid grid-cols-[2rem_1fr] gap-3 text-sm text-[#77777d]">
        <span className="mt-1 flex size-7 items-center justify-center rounded-md bg-[#111] text-white">
            <Sparkles className="size-3.5" />
        </span>
        <div className="flex items-center gap-2 py-2">
            <Loader2 className="size-4 animate-spin text-[#2d72d2]" />
            <span role="status">Thinking...</span>
        </div>
    </div>
)

const ChatComposer = observer(
    ({
        chatLimits,
        onSendMessage,
    }: {
        chatLimits: ChatLimits
        onSendMessage: (content: string) => void
    }) => {
        const chat = chatStore
        const draft = chat.draft

        const submitMessage = (event: React.SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault()
            onSendMessage(draft)
        }

        return (
            <div className="sticky bottom-0 -mx-2 bg-gradient-to-t from-[#fbfcfd] via-[#fbfcfd] to-transparent pb-4 pt-8">
                <form className="mx-auto max-w-3xl" onSubmit={submitMessage}>
                    <div className="rounded-2xl border border-black/10 bg-white/90 p-2 shadow-[0_16px_50px_rgb(15_23_42/0.08)] backdrop-blur-xl focus-within:border-[#9dbdff] focus-within:ring-4 focus-within:ring-[#9dbdff]/20">
                        {chat.queuedMessages.length > 0 ? <QueuedMessages /> : null}
                        <textarea
                            className="max-h-48 min-h-16 w-full resize-none bg-transparent px-3 py-2 text-base leading-6 text-[#26272b] outline-none placeholder:text-[#9a9ba1]"
                            maxLength={chatLimits.maxMessageChars}
                            onBlur={() => chat.flushPersistedInput()}
                            onChange={(event) => chat.setDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (
                                    event.key !== 'Enter' ||
                                    event.shiftKey ||
                                    event.nativeEvent.isComposing
                                )
                                    return
                                event.preventDefault()
                                onSendMessage(draft)
                            }}
                            placeholder="Ask about performance, recipes, keywords, or changes..."
                            value={draft}
                        />
                        <div className="flex items-center justify-end gap-3 px-2 pb-1">
                            <button
                                className="inline-flex size-9 items-center justify-center rounded-xl bg-[#111] text-white shadow-[0_6px_18px_rgb(15_23_42/0.16)] transition hover:bg-[#2b2c30] disabled:cursor-not-allowed disabled:opacity-45"
                                disabled={!draft.trim()}
                                title="Send message"
                                type="submit"
                            >
                                {chat.isSending ? (
                                    <MessageSquarePlus className="size-4" />
                                ) : (
                                    <Send className="size-4" />
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        )
    },
)

const QueuedMessages = observer(() => {
    const chat = chatStore

    return (
        <div className="mb-2 max-h-40 overflow-y-auto rounded-xl border border-[#d8e2f7] bg-[#f6f9ff] p-1.5">
            <div className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-[#536177]">
                <Clock3 className="size-3.5" />
                <span>
                    {String(chat.queuedMessages.length)} queued{' '}
                    {chat.queuedMessages.length === 1 ? 'message' : 'messages'}
                </span>
            </div>
            <div className="space-y-1">
                {chat.queuedMessages.map((message) => (
                    <div
                        className="flex min-h-9 items-center gap-2 rounded-lg border border-white/80 bg-white px-2 py-1.5 text-sm text-[#30333a] shadow-[0_1px_1px_rgb(15_23_42/0.04)]"
                        key={message.id}
                    >
                        <p className="min-w-0 flex-1 truncate">{message.content}</p>
                        <button
                            aria-label="Cancel queued message"
                            className="flex size-7 shrink-0 items-center justify-center rounded-md text-[#7a8291] transition hover:bg-[#f0f3f8] hover:text-[#202124]"
                            onClick={() => chat.cancelQueuedMessage(message.id)}
                            title="Cancel queued message"
                            type="button"
                        >
                            <X className="size-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    )
})

const stringifyToolValue = (value: unknown): string => {
    if (value == null) return ''
    if (typeof value === 'string') return value
    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const getToolDisplayName = (toolName: string | undefined): string => {
    if (!toolName) return 'Tool call'
    return toolName.replace(/[_-]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const formatPreviewValue = (value: unknown): string => {
    if (value == null) return '-'
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return '-'
        return Math.abs(value) >= 1000
            ? value.toLocaleString()
            : String(Math.round(value * 10000) / 10000)
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'string') return value
    return stringifyToolValue(value)
}

const getPreviewRows = (toolCall: ChatToolCall): Record<string, unknown>[] => {
    const rows = toolCall.result?.rows
    return Array.isArray(rows) ? rows.filter(isRecordValue) : []
}

const getPreviewColumns = (toolCall: ChatToolCall, rows: Record<string, unknown>[]) => {
    const configuredColumns = toolCall.result?.columns
    const rowKeys = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
    const rowColumns = rowKeys.map((key) => ({
        key,
        label: key
            .replace(/^___/, '$')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    }))

    if (Array.isArray(configuredColumns)) {
        const configured = configuredColumns
            .filter(isRecordValue)
            .map((column) => ({
                key: typeof column.key === 'string' ? column.key : '',
                label: typeof column.label === 'string' ? column.label : undefined,
            }))
            .filter((column) => column.key)

        return [
            ...configured,
            ...rowColumns.filter(
                (rowColumn) => !configured.some((column) => column.key === rowColumn.key),
            ),
        ]
    }

    return rowColumns
}

const SegmentPreviewToolCall = ({ toolCall, index }: { toolCall: ChatToolCall; index: number }) => {
    const rows = getPreviewRows(toolCall)
    const columns = getPreviewColumns(toolCall, rows)
    const status = toolCall.result?.status
    const rowCount =
        typeof toolCall.result?.rowCount === 'number' ? toolCall.result.rowCount : rows.length
    const totalCount =
        typeof toolCall.result?.totalCount === 'number' ? toolCall.result.totalCount : undefined

    return (
        <details
            className="overflow-hidden rounded-xl border border-black/10 bg-white/80 shadow-[0_4px_16px_rgb(15_23_42/0.035)]"
            key={`${toolCall.name ?? 'tool'}-${String(index)}`}
        >
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs text-[#6f7076] marker:hidden">
                <span className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-[#47b881]" />
                    <span className="font-medium text-[#333438]">Segment Preview</span>
                    {typeof status === 'string' ? (
                        <span className="text-[#96979d]">{status}</span>
                    ) : null}
                </span>
                <span className="rounded-full bg-[#f3f4f6] px-2 py-0.5">
                    {totalCount === undefined
                        ? `${rowCount.toLocaleString()} rows`
                        : `${rowCount.toLocaleString()} of ${totalCount.toLocaleString()} rows`}
                </span>
            </summary>
            <details className="border-t border-black/10">
                <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-[#6f7076] marker:hidden">
                    Preview table
                </summary>
                {rows.length > 0 && columns.length > 0 ? (
                    <div className="max-h-80 overflow-auto border-t border-black/10">
                        <table className="min-w-full border-separate border-spacing-0 text-left text-xs">
                            <thead className="sticky top-0 bg-[#f7f8fa] text-[#666970]">
                                <tr>
                                    {columns.map((column) => (
                                        <th
                                            className="whitespace-nowrap border-b border-black/10 px-3 py-2 font-medium"
                                            key={column.key}
                                        >
                                            {column.label ?? column.key}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5">
                                {rows.map((row, rowIndex) => (
                                    <tr
                                        className="align-top odd:bg-white even:bg-[#fbfcfd]"
                                        key={`preview-row-${String(rowIndex)}`}
                                    >
                                        {columns.map((column) => (
                                            <td
                                                className="max-w-56 whitespace-nowrap px-3 py-2 text-[#33363d]"
                                                key={`${String(rowIndex)}-${column.key}`}
                                                title={formatPreviewValue(row[column.key])}
                                            >
                                                <span className="block truncate">
                                                    {formatPreviewValue(row[column.key])}
                                                </span>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="border-t border-black/10 px-3 py-3 text-sm text-[#77777d]">
                        No rows matched this segment.
                    </div>
                )}
            </details>
            <details className="border-t border-black/10">
                <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-[#6f7076] marker:hidden">
                    Raw tool result
                </summary>
                <div className="px-3 pb-3">
                    <pre className="overflow-x-auto rounded-lg bg-[#0f1117] p-3 text-xs leading-5 text-[#eef2ff]">
                        <code>{stringifyToolValue(toolCall)}</code>
                    </pre>
                </div>
            </details>
        </details>
    )
}

const ToolCallList = ({ toolCalls }: { toolCalls: ChatToolCall[] }) => (
    <div className="mb-4 space-y-2">
        {toolCalls.map((toolCall, index) => {
            if (toolCall.name === 'preview_segment_query') {
                return (
                    <SegmentPreviewToolCall
                        index={index}
                        key={`${toolCall.name}-${String(index)}`}
                        toolCall={toolCall}
                    />
                )
            }

            const rowCount = toolCall.result?.rowCount ?? toolCall.result?.row_count
            const status = toolCall.result?.status

            return (
                <details
                    className="group rounded-xl border border-black/10 bg-white/70 shadow-[0_4px_16px_rgb(15_23_42/0.035)]"
                    key={`${toolCall.name ?? 'tool'}-${String(index)}`}
                >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-xs text-[#6f7076] marker:hidden">
                        <span className="flex items-center gap-2">
                            <span className="size-2 rounded-full bg-[#9dbdff]" />
                            <span className="font-medium text-[#333438]">
                                {getToolDisplayName(toolCall.name)}
                            </span>
                            {typeof status === 'string' ? (
                                <span className="text-[#96979d]">{status}</span>
                            ) : null}
                        </span>
                        {typeof rowCount === 'number' ? (
                            <span className="rounded-full bg-[#f3f4f6] px-2 py-0.5">
                                {String(rowCount)} rows
                            </span>
                        ) : null}
                    </summary>
                    <div className="border-t border-black/10 px-3 py-3">
                        <pre className="overflow-x-auto rounded-lg bg-[#0f1117] p-3 text-xs leading-5 text-[#eef2ff]">
                            <code>{stringifyToolValue(toolCall)}</code>
                        </pre>
                    </div>
                </details>
            )
        })}
    </div>
)

const isTableDivider = (line: string): boolean =>
    /^\s*\|?[\s:-]+\|[\s|:-]*\|?\s*$/.test(line) && line.includes('-')

const isTableRow = (line: string): boolean =>
    line.trim().startsWith('|') && line.trim().endsWith('|') && line.includes('|')

const parseHeading = (line: string): { level: 1 | 2 | 3 | 4; content: string } | null => {
    const match = line.match(/^(#{1,4})\s+(.+?)\s*#*\s*$/)
    if (!match) return null

    return {
        level: match[1].length as 1 | 2 | 3 | 4,
        content: match[2],
    }
}

const parseTableCells = (line: string): string[] =>
    line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim())

const isHorizontalRule = (line: string): boolean => /^\s*---\s*$/.test(line)

const parseMarkdownBlocks = (content: string): MarkdownBlock[] => {
    const blocks: MarkdownBlock[] = []
    const lines = content.split('\n')
    let index = 0

    while (index < lines.length) {
        const line = lines[index] ?? ''
        if (!line.trim()) {
            index += 1
            continue
        }

        const fenceMatch = line.match(/^```(\w+)?\s*$/)
        if (fenceMatch) {
            const codeLines: string[] = []
            index += 1

            while (index < lines.length && !/^```\s*$/.test(lines[index] ?? '')) {
                codeLines.push(lines[index] ?? '')
                index += 1
            }

            if (index < lines.length) index += 1
            blocks.push({
                type: 'code',
                language: fenceMatch[1] ?? null,
                content: codeLines.join('\n'),
            })
            continue
        }

        const heading = parseHeading(line.trim())
        if (heading) {
            blocks.push({ type: 'heading', ...heading })
            index += 1
            continue
        }

        if (isHorizontalRule(line)) {
            blocks.push({ type: 'rule' })
            index += 1
            continue
        }

        if (
            isTableRow(line) &&
            index + 1 < lines.length &&
            isTableDivider(lines[index + 1] ?? '')
        ) {
            const headers = parseTableCells(line)
            const rows: string[][] = []
            index += 2

            while (index < lines.length && isTableRow(lines[index] ?? '')) {
                rows.push(parseTableCells(lines[index] ?? ''))
                index += 1
            }

            blocks.push({ type: 'table', headers, rows })
            continue
        }

        if (/^\s*[-*]\s+/.test(line)) {
            const items: string[] = []
            while (index < lines.length && /^\s*[-*]\s+/.test(lines[index] ?? '')) {
                items.push((lines[index] ?? '').replace(/^\s*[-*]\s+/, '').trim())
                index += 1
            }
            blocks.push({ type: 'list', items })
            continue
        }

        const paragraphLines = [line.trim()]
        index += 1

        while (
            index < lines.length &&
            lines[index]?.trim() &&
            !/^```/.test(lines[index] ?? '') &&
            !parseHeading(lines[index]?.trim() ?? '') &&
            !isHorizontalRule(lines[index] ?? '') &&
            !/^\s*[-*]\s+/.test(lines[index] ?? '') &&
            !(isTableRow(lines[index] ?? '') && isTableDivider(lines[index + 1] ?? ''))
        ) {
            paragraphLines.push((lines[index] ?? '').trim())
            index += 1
        }

        blocks.push({ type: 'paragraph', content: paragraphLines.join(' ') })
    }

    return blocks
}

const renderInlineMarkdown = (content: string) => {
    const parts = content.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter(Boolean)

    return parts.map((part, index) => {
        if (part.startsWith('`') && part.endsWith('`')) {
            return (
                <code
                    className="rounded-md border border-black/10 bg-[#f5f6f8] px-1.5 py-0.5 font-mono text-[0.9em] text-[#24262b]"
                    key={`${part}-${String(index)}`}
                >
                    {part.slice(1, -1)}
                </code>
            )
        }

        if (part.startsWith('**') && part.endsWith('**')) {
            return (
                <strong className="font-semibold text-[#232428]" key={`${part}-${String(index)}`}>
                    {part.slice(2, -2)}
                </strong>
            )
        }

        return <span key={`${part}-${String(index)}`}>{part}</span>
    })
}

const headingClasses: Record<1 | 2 | 3 | 4, string> = {
    1: 'text-xl font-semibold leading-8 text-[#202124]',
    2: 'text-lg font-semibold leading-7 text-[#202124]',
    3: 'text-base font-semibold leading-7 text-[#24262b]',
    4: 'text-sm font-semibold leading-6 text-[#303136]',
}

const MarkdownMessage = ({ content }: { content: string }) => {
    const blocks = parseMarkdownBlocks(content)

    return (
        <div className="space-y-4 text-sm leading-7 text-[#303136]">
            {blocks.map((block, index) => {
                if (block.type === 'code') {
                    return (
                        <div
                            className="overflow-hidden rounded-xl border border-black/10 bg-[#0f1117] shadow-[0_8px_24px_rgb(15_23_42/0.08)]"
                            key={`code-${String(index)}`}
                        >
                            <div className="flex h-8 items-center justify-between border-b border-white/10 px-3 text-xs text-white/52">
                                <span className="font-mono">{block.language ?? 'code'}</span>
                            </div>
                            <pre className="overflow-x-auto p-4 text-sm leading-6 text-[#eef2ff]">
                                <code>{block.content}</code>
                            </pre>
                        </div>
                    )
                }

                if (block.type === 'heading') {
                    const headingContent = renderInlineMarkdown(block.content)
                    const headingClassName = headingClasses[block.level]
                    if (block.level === 1)
                        return (
                            <h1 className={headingClassName} key={`heading-${String(index)}`}>
                                {headingContent}
                            </h1>
                        )
                    if (block.level === 2)
                        return (
                            <h2 className={headingClassName} key={`heading-${String(index)}`}>
                                {headingContent}
                            </h2>
                        )
                    if (block.level === 3)
                        return (
                            <h3 className={headingClassName} key={`heading-${String(index)}`}>
                                {headingContent}
                            </h3>
                        )
                    return (
                        <h4 className={headingClassName} key={`heading-${String(index)}`}>
                            {headingContent}
                        </h4>
                    )
                }

                if (block.type === 'table') {
                    return (
                        <div
                            className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-[0_6px_22px_rgb(15_23_42/0.04)]"
                            key={`table-${String(index)}`}
                        >
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-left text-sm">
                                    <thead className="bg-[#f6f7f9] text-xs uppercase tracking-[0.04em] text-[#73747a]">
                                        <tr>
                                            {block.headers.map((header) => (
                                                <th
                                                    className="border-b border-black/10 px-3 py-2 font-medium"
                                                    key={header}
                                                >
                                                    {renderInlineMarkdown(header)}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {block.rows.map((row, rowIndex) => (
                                            <tr
                                                className="border-b border-black/5 last:border-0"
                                                key={`row-${String(rowIndex)}`}
                                            >
                                                {block.headers.map((header, cellIndex) => (
                                                    <td
                                                        className="px-3 py-2 align-top"
                                                        key={`${header}-${String(cellIndex)}`}
                                                    >
                                                        {renderInlineMarkdown(row[cellIndex] ?? '')}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                }

                if (block.type === 'list') {
                    return (
                        <ul className="space-y-1.5 pl-5" key={`list-${String(index)}`}>
                            {block.items.map((item) => (
                                <li className="list-disc pl-1" key={item}>
                                    {renderInlineMarkdown(item)}
                                </li>
                            ))}
                        </ul>
                    )
                }

                if (block.type === 'rule') {
                    return <hr className="my-5 border-black/10" key={`rule-${String(index)}`} />
                }

                return (
                    <p className="max-w-none" key={`paragraph-${String(index)}`}>
                        {renderInlineMarkdown(block.content)}
                    </p>
                )
            })}
        </div>
    )
}
